class CollageModal extends HTMLElement {
  constructor() {
    super();
    this.triggers = [];
    this.currentIndex = 0;
    this.mediaEl = null;
    this.creatorEl = null;
    this.productCardEl = null;
  }

  connectedCallback() {
    // Guard: appendChild abaixo re-dispara connectedCallback de forma síncrona,
    // o que duplicava todos os listeners (mute virava no-op por double-toggle).
    if (this._initialized) return;
    this._initialized = true;

    // Move pro body para escapar de stacking contexts (transform/filter) do parent
    // section, que faziam o mobile-dock ficar visualmente acima do modal.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.mediaEl = this.querySelector('.yv-collage-modal-media');
    this.creatorEl = this.querySelector('.yv-collage-modal-creator-handle');
    this.productCardEl = this.querySelector('.yv-collage-modal-product');

    this.querySelector('.yv-collage-modal-close')?.addEventListener('click', () => this.close());
    this.querySelector('[data-modal-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-modal-next]')?.addEventListener('click', () => this.go(1));

    // Clique fora do dialog fecha o modal (área escura do overlay).
    // Em mobile o dialog cobre a tela inteira, então não tem área pra clicar fora.
    this.addEventListener('click', (e) => {
      if (!this.classList.contains('is-open')) return;
      if (!e.target.closest('.yv-collage-modal-dialog')) {
        this.close();
      }
    });

    // Áudio ON por padrão. Se iOS bloquear, render() faz fallback pra muted.
    this._muted = false;
    this.muteBtn = this.querySelector('.yv-collage-modal-mute');
    const doMuteToggle = () => {
      this._muted = !this._muted;
      const video = this.mediaEl?.querySelector('video');
      if (video) {
        const wasPlaying = !video.paused;
        video.muted = this._muted;
        if (wasPlaying) {
          // iOS pausa async ao alternar muted enquanto toca. Múltiplas
          // tentativas em momentos diferentes pra ganhar do auto-pause.
          // play() síncrono mantém user activation; os timeouts cobrem
          // pauses que vêm depois da renegociação do audio session.
          const restore = () => { if (video.paused) video.play().catch(() => {}); };
          restore();
          requestAnimationFrame(restore);
          setTimeout(restore, 50);
          setTimeout(restore, 150);
          setTimeout(restore, 350);
        }
      }
      this._syncMuteButton();
    };
    // Um handler só pra evitar double-toggle iOS (touchend + click sintético).
    // pointerup cobre touch e mouse na mesma chamada.
    this.muteBtn?.addEventListener('pointerup', (e) => {
      e.stopPropagation();
      e.preventDefault();
      doMuteToggle();
    });
    // Fallback pra browsers/contextos sem PointerEvent
    this.muteBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (window.PointerEvent) return; // pointerup já tratou
      doMuteToggle();
    });

    // Tap no media alterna play/pause. Mute button é sibling do media —
    // eventos no mute NÃO chegam aqui. Usa touchend (mobile) e click (desktop)
    // separadamente pra evitar double-fire que pointer events causavam no iOS.
    if (this.mediaEl) {
      let tapStartX = 0, tapStartY = 0, tapMoved = false;
      const togglePlayPause = () => {
        const video = this._video;
        if (!video) return;
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      };
      this.mediaEl.addEventListener('touchstart', (e) => {
        tapStartX = e.touches[0].clientX;
        tapStartY = e.touches[0].clientY;
        tapMoved = false;
      }, { passive: true });
      this.mediaEl.addEventListener('touchmove', (e) => {
        const dx = Math.abs(e.touches[0].clientX - tapStartX);
        const dy = Math.abs(e.touches[0].clientY - tapStartY);
        if (dx > 10 || dy > 10) tapMoved = true;
      }, { passive: true });
      this.mediaEl.addEventListener('touchend', (e) => {
        if (tapMoved) return; // swipe — dialog cuida
        // Hit-test final: se o toque terminou em cima de um botão/controle,
        // ignora o toggle. Cobre o caso de tocar no mute mas o touch target
        // ter sido reportado como media (acontece em hits na borda).
        const t = e.changedTouches[0];
        const el = document.elementFromPoint(t.clientX, t.clientY);
        if (el && el.closest('.yv-collage-modal-mute, .yv-collage-modal-close, .yv-collage-modal-side, .yv-collage-modal-product, .yv-collage-modal-creator')) {
          return;
        }
        e.preventDefault(); // bloqueia synthetic click depois do touchend
        togglePlayPause();
      }, { passive: false });
      // Desktop (sem touch): click é a única forma. No mobile, o
      // preventDefault no touchend bloqueia o click sintético.
      this.mediaEl.addEventListener('click', (e) => {
        e.stopPropagation();
        togglePlayPause();
      });
    }

    // Swipe vertical no mobile (estilo Reels)
    const dialog = this.querySelector('.yv-collage-modal-dialog');
    if (dialog) {
      let touchStartY = 0;
      let touchStartX = 0;
      let touchActive = false;
      // Não excluir <video> aqui: o vídeo cobre a área toda do modal e estava
      // bloqueando o swipe. Threshold de 50px já filtra taps de controle nativo.
      dialog.addEventListener('touchstart', (e) => {
        if (e.target.closest('.yv-collage-modal-product, .yv-collage-modal-close, .yv-collage-modal-side, .yv-collage-modal-mute')) return;
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
        touchActive = true;
      }, { passive: true });
      dialog.addEventListener('touchend', (e) => {
        if (!touchActive) return;
        touchActive = false;
        const deltaY = e.changedTouches[0].clientY - touchStartY;
        const deltaX = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(deltaY) < 50 || Math.abs(deltaX) > Math.abs(deltaY)) return;
        if (deltaY < 0) this.go(1);
        else this.go(-1);
      }, { passive: true });
    }
    this.productCardEl?.addEventListener('click', (e) => {
      if (e.target.closest('.yv-collage-modal-product-cta') || e.currentTarget === e.target) {
        this.openQuickAdd();
      } else if (e.target.closest('.yv-collage-modal-product-info, .yv-collage-modal-product-image')) {
        this.openQuickAdd();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') this.go(1);
    });

    const sectionId = this.dataset.sectionId;
    this.triggers = Array.from(
      document.querySelectorAll(`[data-collage-trigger][data-section-id="${sectionId}"]`)
    );

    // Event delegation no document: garante que click em qualquer descendente
    // do card (imagem, poster, overlay, texto, botão antigo do tema) abre o modal.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(`[data-collage-trigger][data-section-id="${sectionId}"]`);
      if (!trigger) return;
      e.preventDefault();
      const idx = this.triggers.indexOf(trigger);
      if (idx >= 0) this.open(idx);
    }, true);

    // Acessibilidade: abrir via Enter/Espaço quando o card tem foco
    this.triggers.forEach((trigger, idx) => {
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open(idx);
        }
      });
    });
  }

  open(index) {
    this.currentIndex = index;
    this.render();
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('yv-collage-modal-open');
    this._maybeShowSwipeHint();
  }

  _maybeShowSwipeHint() {
    try {
      if (localStorage.getItem('yv-collage-swipe-hint-seen') === '1') return;
    } catch (e) { /* localStorage indisponível, mostra mesmo assim */ }
    this.classList.add('is-hint-visible');
    const dismiss = () => {
      this.classList.remove('is-hint-visible');
      try { localStorage.setItem('yv-collage-swipe-hint-seen', '1'); } catch (e) {}
      this.removeEventListener('touchstart', dismiss);
      this.removeEventListener('click', dismissOnClick);
      clearTimeout(autoDismiss);
    };
    const dismissOnClick = () => dismiss();
    // Dispensa no primeiro toque (mobile) ou click (desktop). Auto-dispensa
    // em 4s caso o usuário fique parado.
    this.addEventListener('touchstart', dismiss, { passive: true, once: true });
    this.addEventListener('click', dismissOnClick, { once: true });
    const autoDismiss = setTimeout(dismiss, 4000);
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('yv-collage-modal-open');
    this.clearMedia();
  }

  go(delta) {
    if (this._transitioning) return;
    const next = this.currentIndex + delta;
    if (next < 0 || next >= this.triggers.length) return;
    this._transitioning = true;

    const trigger = this.triggers[next];
    const data = trigger.dataset;
    const dur = 320;
    const ease = 'cubic-bezier(0.32, 0.72, 0, 1)';
    const slideOut = delta > 0 ? '-100%' : '100%';
    const slideIn = delta > 0 ? '100%' : '-100%';

    // Find/wrap conteúdo atual em um slide. Se já tem .yv-slide (não é a
    // primeira transição), reusa. Senão, embrulha o conteúdo direto do mediaEl.
    let oldSlide = this.mediaEl.querySelector(':scope > .yv-slide');
    if (!oldSlide) {
      oldSlide = document.createElement('div');
      oldSlide.className = 'yv-slide';
      oldSlide.style.cssText = 'position:absolute;inset:0;will-change:transform;';
      Array.from(this.mediaEl.children).forEach(child => {
        if (!child.classList.contains('yv-collage-modal-play-icon')) {
          oldSlide.appendChild(child);
        }
      });
      this.mediaEl.appendChild(oldSlide);
    }
    // Muta o vídeo antigo já — libera audio session pro novo no iOS
    const oldVideo = oldSlide.querySelector('video');
    if (oldVideo) oldVideo.muted = true;

    // Cria novo slide já posicionado fora da tela com o vídeo novo
    // (vídeo + poster ficam visíveis durante o slide-in inteiro).
    const newSlide = document.createElement('div');
    newSlide.className = 'yv-slide';
    newSlide.style.cssText = `position:absolute;inset:0;transform:translateY(${slideIn});will-change:transform;`;
    let newVideo = null;
    if (data.mediaType === 'video' && data.videoSrc) {
      newVideo = document.createElement('video');
      newVideo.src = data.videoSrc;
      if (data.videoPoster) newVideo.poster = data.videoPoster;
      newVideo.controls = false;
      newVideo.playsInline = true;
      newVideo.setAttribute('playsinline', '');
      newVideo.setAttribute('webkit-playsinline', '');
      newVideo.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback noplaybackrate');
      newVideo.disablePictureInPicture = true;
      newVideo.disableRemotePlayback = true;
      newVideo.loop = true;
      // Começa no estado desejado (com som se o usuário não tava mutado).
      // Evita mudar muted DEPOIS, que causa o auto-pause do iOS visível
      // como flicker no fim da animação.
      newVideo.muted = this._muted;
      newVideo.preload = 'auto';
      newSlide.appendChild(newVideo);
      // Try play. Se iOS bloquear unmuted autoplay (raro em gesto), cai pra muted.
      newVideo.play().catch(() => {
        newVideo.muted = true;
        this._muted = true;
        this._syncMuteButton();
        newVideo.play().catch(() => {});
      });
    } else if (data.imageSrc) {
      const img = document.createElement('img');
      img.src = data.imageSrc;
      img.alt = data.imageAlt || '';
      // Inline object-fit pra evitar zoom durante a transição (a classe
      // is-photo do mediaEl pode oscilar entre slides foto/vídeo).
      img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';
      newSlide.appendChild(img);
    }
    this.mediaEl.appendChild(newSlide);

    // Anima ambos simultaneamente
    requestAnimationFrame(() => {
      oldSlide.style.transition = `transform ${dur}ms ${ease}`;
      newSlide.style.transition = `transform ${dur}ms ${ease}`;
      oldSlide.style.transform = `translateY(${slideOut})`;
      newSlide.style.transform = 'translateY(0)';
    });

    setTimeout(() => {
      // Cleanup do antigo — remove o wrapper INTEIRO (sem mover children
      // do novo, o que pausaria o vídeo).
      if (oldVideo) {
        oldVideo.pause();
        oldVideo.removeAttribute('src');
        oldVideo.load();
      }
      oldSlide.remove();
      // newSlide permanece em mediaEl como o slide atual.

      // Atualiza estado
      this.currentIndex = next;
      this._video = newVideo;
      this.mediaEl.classList.remove('is-paused');

      if (newVideo) {
        newVideo.addEventListener('play', () => this.mediaEl.classList.remove('is-paused'));
        newVideo.addEventListener('pause', () => this.mediaEl.classList.add('is-paused'));
        // Sem mudança de muted aqui — já foi criado no estado correto.
      }

      // Atualiza overlays (creator, product card, nav buttons)
      this._updateOverlays(data);
      this._syncMuteButton();
      this._transitioning = false;
    }, dur + 30);
  }

  _updateOverlays(data) {
    if (this.creatorEl) {
      this.creatorEl.textContent = data.creator || '';
      this.creatorEl.style.display = data.creator ? '' : 'none';
    }
    if (this.productCardEl) {
      if (data.productHandle) {
        this.productCardEl.style.display = '';
        const titleEl = this.productCardEl.querySelector('.yv-collage-modal-product-title');
        const variantEl = this.productCardEl.querySelector('.yv-collage-modal-product-variant');
        const priceEl = this.productCardEl.querySelector('.yv-collage-modal-product-price');
        const imgEl = this.productCardEl.querySelector('.yv-collage-modal-product-image img');
        if (titleEl) titleEl.textContent = data.productTitle || '';
        if (variantEl) {
          variantEl.textContent = data.productVariant || '';
          variantEl.style.display = data.productVariant ? '' : 'none';
        }
        if (priceEl) priceEl.innerHTML = data.productPrice || '';
        if (imgEl) {
          imgEl.src = data.productImage || '';
          imgEl.alt = data.productTitle || '';
        }
      } else {
        this.productCardEl.style.display = 'none';
      }
    }
    const prevBtn = this.querySelector('[data-modal-prev]');
    const nextBtn = this.querySelector('[data-modal-next]');
    if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
    if (nextBtn) nextBtn.disabled = this.currentIndex === this.triggers.length - 1;
  }

  clearMedia() {
    const video = this.mediaEl.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    this._video = null;
    this.mediaEl.innerHTML = '';
    this.mediaEl.classList.remove('is-photo', 'is-paused');
  }

  render() {
    const trigger = this.triggers[this.currentIndex];
    if (!trigger) return;
    const data = trigger.dataset;

    this.clearMedia();
    if (data.mediaType === 'video' && data.videoSrc) {
      const video = document.createElement('video');
      video.src = data.videoSrc;
      if (data.videoPoster) video.poster = data.videoPoster;
      // Sem controles nativos: o iOS injeta botões de fullscreen/AirPlay/PiP
      // que sobrepõem a UI do modal. Damos play/pause e som via tap custom.
      video.controls = false;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', '');
      video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback noplaybackrate');
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.loop = true;
      video.muted = this._muted;
      this.mediaEl.appendChild(video);

      // Ícone de play que aparece quando o vídeo está pausado.
      const playIcon = document.createElement('div');
      playIcon.className = 'yv-collage-modal-play-icon';
      playIcon.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7z"/></svg>';
      this.mediaEl.appendChild(playIcon);

      this._video = video;

      video.addEventListener('play', () => this.mediaEl.classList.remove('is-paused'));
      video.addEventListener('pause', () => this.mediaEl.classList.add('is-paused'));

      // Truque do autoplay com som no iOS/Android: começa muted (sempre
      // permitido), e dentro da MESMA ativação de usuário muda pra unmuted.
      // Browsers consideram isso parte do mesmo gesto e liberam o áudio.
      video.muted = true;
      const tryPlay = video.play();
      const unmuteIfWanted = () => {
        if (this._muted) return;
        video.muted = false;
        video.volume = 1;
        const p = video.play();
        if (p && p.catch) {
          p.catch(() => {
            video.muted = true;
            video.volume = 0;
            this._muted = true;
            this._syncMuteButton();
          });
        }
      };
      if (tryPlay && tryPlay.then) {
        tryPlay.then(unmuteIfWanted).catch(() => {
          // Nem muted rodou: tenta uma última vez no canplay
          video.addEventListener('canplay', () => video.play().then(unmuteIfWanted).catch(() => {}), { once: true });
        });
      } else {
        unmuteIfWanted();
      }
      this._syncMuteButton();
    } else if (data.imageSrc) {
      const img = document.createElement('img');
      img.src = data.imageSrc;
      img.alt = data.imageAlt || '';
      this.mediaEl.appendChild(img);
      this.mediaEl.classList.add('is-photo');
    }

    if (this.creatorEl) {
      this.creatorEl.textContent = data.creator || '';
      this.creatorEl.style.display = data.creator ? '' : 'none';
    }

    if (this.productCardEl) {
      if (data.productHandle) {
        this.productCardEl.style.display = '';
        const titleEl = this.productCardEl.querySelector('.yv-collage-modal-product-title');
        const variantEl = this.productCardEl.querySelector('.yv-collage-modal-product-variant');
        const priceEl = this.productCardEl.querySelector('.yv-collage-modal-product-price');
        const imgEl = this.productCardEl.querySelector('.yv-collage-modal-product-image img');
        if (titleEl) titleEl.textContent = data.productTitle || '';
        if (variantEl) {
          variantEl.textContent = data.productVariant || '';
          variantEl.style.display = data.productVariant ? '' : 'none';
        }
        if (priceEl) priceEl.innerHTML = data.productPrice || '';
        if (imgEl) {
          imgEl.src = data.productImage || '';
          imgEl.alt = data.productTitle || '';
        }
      } else {
        this.productCardEl.style.display = 'none';
      }
    }

    const prevBtn = this.querySelector('[data-modal-prev]');
    const nextBtn = this.querySelector('[data-modal-next]');
    if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
    if (nextBtn) nextBtn.disabled = this.currentIndex === this.triggers.length - 1;
  }

  _syncMuteButton() {
    if (!this.muteBtn) return;
    const isVideo = !!this.mediaEl?.querySelector('video');
    this.muteBtn.style.display = isVideo ? '' : 'none';
    this.muteBtn.dataset.muted = this._muted ? 'true' : 'false';
    this.muteBtn.setAttribute('aria-label', this._muted ? 'Ativar som' : 'Silenciar');
  }

  openQuickAdd() {
    const trigger = this.triggers[this.currentIndex];
    if (!trigger) return;
    const template = trigger.querySelector('template[data-quick-add-template]');
    const popup = document.querySelector('.quick-add-popup');
    const body = popup?.querySelector('[quick-add-body]');
    if (!template || !popup || !body) {
      if (trigger.dataset.productUrl) window.location.href = trigger.dataset.productUrl;
      return;
    }
    body.innerHTML = '';
    body.appendChild(template.content.cloneNode(true));
    document.body.classList.add('quick-add-popup-open');
    popup.classList.add('show');
    if (window.Shopify && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
    this.close();
  }
}

if (!customElements.get('collage-modal')) {
  customElements.define('collage-modal', CollageModal);
}
